import React, { useState, useContext } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { NumericFormat } from 'react-number-format';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import formatMoney from '../../utils/formatMoney';
import invoiceDataSelector from '../../selectors/invoice';
import { InvoiceContext } from '../context/invoiceData';

const DollarCustomInput = (props) => {
  // eslint-disable-next-line react/prop-types
  const { inputRef, onChange, ...other } = props;

  return (
    <NumericFormat
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...other}
      getInputRef={inputRef}
      onValueChange={(values) => {
        onChange({
          target: {
            // eslint-disable-next-line react/prop-types
            name: props.name,
            value: values.value,
          },
        });
      }}
      decimalScale={2}
      thousandSeparator
      prefix="$"
    />
  );
};

const PaymentInput = (props) => {
  const {
    agreement,
    submitting,
    acceptedAgreement,
    allowPartialPayment,
    allowOverPayment,
    remainingBalance,
    paymentAmount,
    invoiceID,
    paymentAmountTouched,
    paymentAmountError,
    toggleAcceptedAgreement,
    handleChange,
    handleBlur,
    surchargeFlag,
    cardLength,
  } = props;

  const selectorData = useSelector(invoiceDataSelector);
  const { surchargeRate, surchargeAddition, surchargeLabel } = selectorData;

  const { invoicingData, isAch } = useContext(InvoiceContext);

  const isPaymentDisabled = () => (allowPartialPayment === false && allowOverPayment === false) || parseFloat(remainingBalance) === 0 || invoiceID === '';

  const [currentPayment, setCurrentPayment] = useState(paymentAmount);

  const [totalPayment, setTotalPayment] = useState('');

  const [surchargeDisplay, setSurchargeDisplay] = useState({ display: 'none' });

  return (
    <>
      <FormControlLabel
        style={{ marginTop: '50px' }}
        checked={acceptedAgreement}
        control={<Checkbox color="primary" name="payment-agreement" />}
        label={agreement}
        onChange={() => { toggleAcceptedAgreement(!acceptedAgreement); }}
      />
      <div className="payment-amount-label">Payment Amount</div>
      <div className="pay-now-input-wrapper">
        <TextField
          variant="standard"
          id="payment-amount"
          name="paymentAmount"
          className={isPaymentDisabled() ? 'pay-now-input-disabled' : 'pay-now-input'}
          inputProps={{ style: { fontSize: 30, fontWeight: '600', height: '50px' } }}
          disabled={isPaymentDisabled()}
          value={paymentAmount}
          // eslint-disable-next-line react/jsx-no-duplicate-props
          InputProps={{
            inputComponent: DollarCustomInput,
          }}
          onChange={(e) => {
            const achProductTransaction = invoicingData?.ach_product_transaction;
            const ccProductTransaction = invoicingData?.cc_product_transaction;
            let fee;
            let rate;
            if (isAch) {
              fee = achProductTransaction?.surcharge?.surcharge_fee || 0;
              rate = achProductTransaction?.surcharge?.surcharge_rate || 0;
            } else {
              fee = ccProductTransaction?.surcharge?.surcharge_fee || 0;
              rate = ccProductTransaction?.surcharge?.surcharge_rate || 0;
            }
            const initialSurcharge = Math.round(
              (Number(e.target.value) * (Number(rate) / 100) + Number(fee) + Number.EPSILON) * 100,
            ) / 100;
            handleChange(e);
            setCurrentPayment(initialSurcharge);
            if (e.target.value === 0 || e.target.value === '0' || e.target.value === '0.00') {
              setCurrentPayment('0.00');
            }
            if (e.target.value === '') {
              setCurrentPayment('0.00');
            }
            setTotalPayment(() => initialSurcharge + Number(e.target.value));
            if (e.target.value === 0 || e.target.value === '0' || e.target.value === '0.00') {
              setTotalPayment('0.00');
            }
            if (e.target.value === '') {
              setTotalPayment('0.00');
            }
            if (typeof surchargeRate !== 'undefined' || typeof surchargeAddition !== 'undefined') {
              setSurchargeDisplay({ display: 'inline' });
            }
          }}
          onBlur={(e) => {
            handleBlur(e);
            e.target.value = parseFloat(e.target.value.replace(/,/g, '').replace('$', '')).toFixed(2);
            // eslint-disable-next-line no-restricted-globals
            if (isNaN(e.target.value)) {
              e.target.value = '0.00';
            }
            handleChange(e);
          }}
        />
        <Button
          className="pay-now-button"
          type="submit"
          variant="contained"
          disabled={invoiceID === '' || submitting || !acceptedAgreement || parseFloat(remainingBalance) === 0}
          style={{
            backgroundColor: (invoiceID === '' || !acceptedAgreement || parseFloat(remainingBalance) === 0) ? 'grey' : '#56A700',
          }}
        >
          Pay
        </Button>
        {(paymentAmountError && paymentAmountTouched) ? <div className="input-error">{paymentAmountError}</div> : null}
        {submitting && <CircularProgress size={24} />}
      </div>
      <div id="payment-total-container" style={surchargeDisplay}>
        <div className="partial-fee-list">
          <div><b>{surchargeLabel}</b></div>
          <div>{surchargeFlag && cardLength === 16 ? 0 : formatMoney(currentPayment)}</div>
        </div>
        <div className="total-payment-list">
          <div><b>Payment Total</b></div>
          <div>
            {/* eslint-disable-next-line react/jsx-one-expression-per-line */}
            {
              surchargeFlag && cardLength === 16
                ? formatMoney(paymentAmount)
                : formatMoney(totalPayment)
            }
          </div>
        </div>
      </div>
    </>
  );
};

PaymentInput.propTypes = {
  agreement: PropTypes.string.isRequired,
  submitting: PropTypes.bool.isRequired,
  acceptedAgreement: PropTypes.bool.isRequired,
  allowPartialPayment: PropTypes.bool.isRequired,
  allowOverPayment: PropTypes.bool.isRequired,
  remainingBalance: PropTypes.string.isRequired,
  paymentAmount: PropTypes.string.isRequired,
  invoiceID: PropTypes.string.isRequired,
  paymentAmountTouched: PropTypes.bool,
  paymentAmountError: PropTypes.string,
  toggleAcceptedAgreement: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired,
  // eslint-disable-next-line max-len
  surchargeFlag: PropTypes.bool.isRequired,
  cardLength: PropTypes.string.isRequired,
};

PaymentInput.defaultProps = {
  paymentAmountError: undefined,
  paymentAmountTouched: undefined,
};

export default PaymentInput;
